/**
 * PageHeader — the standard page title block.
 *
 * Renders an optional back-link / breadcrumb slot, an `<h1>` title, an optional
 * description, and a right-aligned `actions` slot (buttons, filters, etc). Pure
 * tokens so it adapts to dark/light.
 *
 *   <PageHeader
 *     title="Tickets"
 *     description="Triage incoming feedback in real time."
 *     actions={<Button>New widget</Button>}
 *   />
 */
import type { ReactElement, ReactNode } from 'react';

import { cn } from './cn.js';

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned actions (buttons, filters). */
  actions?: ReactNode;
  /** Slot above the title — e.g. a back link or breadcrumb. */
  leading?: ReactNode;
  /** Heading level override (defaults to h1). */
  as?: 'h1' | 'h2';
  className?: string;
}

export function PageHeader({
  title,
  description,
  actions,
  leading,
  as: Heading = 'h1',
  className,
}: PageHeaderProps): ReactElement {
  return (
    <div className={cn('mb-6 flex flex-col gap-4', className)}>
      {leading ? <div className="text-sm">{leading}</div> : null}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Heading className="text-xl font-semibold tracking-tight text-text">
            {title}
          </Heading>
          {description ? (
            <p className="text-sm text-muted">{description}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
    </div>
  );
}
